import { AxiosResponse } from "axios";

import { extractErrors } from "./apiErrorUtils";
import { HttpError } from "./HttpError";

/**
 * Shared guard for /admin and patient mutation hooks. The axios interceptor resolves non-2xx
 * responses as if they succeeded (api.ts `return error.response`), so without
 * this react-query would treat a 400/403 as success and fire onSuccess.
 *
 * The throw carries the server's own words: `message` is every distinct
 * message from the body's `errors`, falling back to the bare status when the
 * body had none. The admin portal is internal-only, so the real reason —
 * however unpolished — beats a hand-written generic one.
 *
 * A missing response (network error: the interceptor resolves `undefined`)
 * throws with status 0.
 */
export function ensureOk<T>(response: AxiosResponse<T> | undefined): T {
  if (!response) {
    throw new HttpError(0, "No response received from the server");
  }
  const { status } = response;
  if (status < 200 || status >= 300) {
    const errors = extractErrors(response);
    const detail = Array.from(
      new Set(errors.map((e) => e.message).filter(Boolean))
    ).join(" ");
    throw new HttpError(
      status,
      detail || `Request failed with status ${status}`,
      errors
    );
  }
  return response.data;
}
